import React, {useState, useEffect} from "react";
import {Box, Button, Typography, Paper, Alert, CircularProgress} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useNavigate, useLocation} from "react-router-dom";
import {useAuth} from "../../contexts/AuthContext";
import LanguageSelector from "../../components/common/LanguageSelector";
import {useForm} from "../../utils/useForm";
import {FORM_VALIDATION_CONFIGS} from "../../utils/formValidation";
import FormField from "../../components/common/FormField";
import {GoogleLogin} from "@react-oauth/google";
import {googleLogin} from "../../api/auth";
import {fetchPublicMarketPrices} from "../../api/marketPrice";

const LoginPage: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {login, googleSignIn, isAuthenticated, loading: authLoading} = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [priceTickerLines, setPriceTickerLines] = useState<string[]>(["Shrimp: 100C → ₹250 | 90C → ₹260 | 40C → ₹350"]);
  const [activePriceLine, setActivePriceLine] = useState(0);

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validationConfig: FORM_VALIDATION_CONFIGS.LOGIN,
    onSubmit: async (values) => {
      try {
        await login(values.email, values.password);
      } catch (err: any) {
        console.error("Login error:", err);
        setError(err.response?.data?.message || "Login failed. Please check your credentials.");
        throw err;
      }
    },
    onError: (errors) => {
      console.log("Validation errors:", errors);
    },
  });

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || "/";
      navigate(from, {replace: true});
    }
  }, [isAuthenticated, navigate, location.state]);

  useEffect(() => {
    const loadPublicPrices = async () => {
      try {
        const rows = await fetchPublicMarketPrices(20);
        if (rows.length === 0) return;

        const commodityMap = rows.reduce<Record<string, typeof rows>>((acc, row) => {
          const key = row.commodity?.name || "Other";
          if (!acc[key]) acc[key] = [];
          acc[key].push(row);
          return acc;
        }, {});

        const lines = Object.entries(commodityMap).map(([commodityName, commodityRows]) => {
          const items = commodityRows
            .sort((a, b) => (b.count || 0) - (a.count || 0))
            .slice(0, 3)
            .map((row) => {
              if (row.pricingMode === "COUNT_WISE" && row.count) {
                return `${row.count}C → ₹${Math.round(Number(row.pricePerKg))}`;
              }
              const label = row.label ? `${row.label} → ` : "";
              return `${label}₹${Math.round(Number(row.pricePerKg))}`;
            });
          return `${commodityName}: ${items.join(" | ")}`;
        });

        if (lines.length > 0) {
          setPriceTickerLines(lines);
          setActivePriceLine(0);
        }
      } catch {
        // Keep fallback ticker text when public API is unavailable.
      }
    };

    loadPublicPrices();
  }, []);

  useEffect(() => {
    if (priceTickerLines.length <= 1) return;
    const id = setInterval(() => {
      setActivePriceLine((prev) => (prev + 1) % priceTickerLines.length);
    }, 2500);
    return () => clearInterval(id);
  }, [priceTickerLines]);

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f5f6fa"
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          borderRadius: 2,
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
        }}>
        {/* Language Selector */}
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <LanguageSelector />
        </Box>

        <Box textAlign="center" mb={3}>
          <Typography variant="h5" component="h1" fontWeight="bold" color="primary">
            SmartFarm
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Sign in to your account
          </Typography>
        </Box>

        <Box sx={{mb: 2, p: 1.5, bgcolor: "#f4f8ff", border: "1px solid #dbe7ff", borderRadius: 1}}>
          <Typography variant="body2" sx={{mb: 0.5, fontWeight: 700, color: "primary.main"}}>
            Today's Public Prices:
          </Typography>
          <Box sx={{mb: 0.5, minHeight: 22}}>
            <Typography
              key={activePriceLine}
              variant="body2"
              sx={{
                lineHeight: "22px",
                fontWeight: 600,
                color: "primary.main",
                "@keyframes priceLineSlideFade": {
                  "0%": {opacity: 0, transform: "translateY(8px)"},
                  "100%": {opacity: 1, transform: "translateY(0)"},
                },
                animation: "priceLineSlideFade 420ms ease",
              }}>
              {priceTickerLines[activePriceLine] || priceTickerLines[0]}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Login to see full market prices
          </Typography>
        </Box>

        <form onSubmit={form.handleSubmit}>
          <FormField
            name="email"
            label={t("Email")}
            type="email"
            value={form.values.email}
            onChange={form.handleChange("email")}
            error={form.errors.email}
            required
            autoComplete="email"
            autoFocus
          />

          <FormField
            name="password"
            label={t("Password")}
            type="password"
            value={form.values.password}
            onChange={form.handleChange("password")}
            error={form.errors.password}
            required
            autoComplete="current-password"
          />

          <Box textAlign="right" mt={1} mb={2}>
            <RouterLink to="/forgot-password" style={{color: "#1976d2", textDecoration: "none"}}>
              Forgot password?
            </RouterLink>
          </Box>

          {error && (
            <Alert severity="error" sx={{mt: 2, mb: 1}}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
            <GoogleLogin
              onSuccess={async (credentialResponse) => {
                try {
                  if (credentialResponse.credential) {
                    await googleSignIn(credentialResponse.credential);
                  }
                } catch (err) {
                  console.error("Google Login verification failed", err);
                  setError("Google Login failed");
                }
              }}
              onError={() => {
                console.log('Login Failed');
                setError("Google Login Failed");
              }}
            />
          </Box>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              borderRadius: 1,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
            }}
            type="submit"
            disabled={form.isSubmitting}>
            {form.isSubmitting ? <CircularProgress size={24} color="inherit" /> : t("Sign In")}
          </Button>

          <Button
            component={RouterLink}
            to="/request-access"
            variant="text"
            fullWidth
            sx={{ textTransform: "none" }}
          >
            Request Access
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default LoginPage;
